type Attrs = Record<string, string | number | boolean | undefined>;
type Child = Node | string | null | undefined;

/** Terse element factory; the panel is small enough not to warrant a UI framework. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue;
    if (key === "class") node.className = String(value);
    else if (key === "text") node.textContent = String(value);
    else if (key === "html") node.innerHTML = String(value);
    else node.setAttribute(key, value === true ? "" : String(value));
  }
  for (const child of children) {
    if (child === null || child === undefined) continue;
    node.append(typeof child === "string" ? document.createTextNode(child) : child);
  }
  return node;
}

export function clear(node: HTMLElement): void {
  while (node.firstChild) node.firstChild.remove();
}

export interface SectionHandle {
  root: HTMLElement;
  body: HTMLElement;
  head: HTMLElement;
  setBadge(text: string | null, kind?: "ok" | "warn" | "err"): void;
  setTitle(text: string): void;
}

/** Collapsible group of controls. */
export function section(title: string, options: { collapsed?: boolean } = {}): SectionHandle {
  const chevron = el("span", { class: "chev", text: options.collapsed ? "▸" : "▾" });
  const label = el("span", { class: "grow", text: title });
  const badge = el("span", { class: "badge", hidden: true });
  const head = el("div", { class: "section-head" }, chevron, label, badge);
  const body = el("div", { class: "section-body" });
  const root = el("div", { class: "section" }, head, body);
  root.dataset.collapsed = String(Boolean(options.collapsed));

  head.addEventListener("click", () => {
    const collapsed = root.dataset.collapsed !== "true";
    root.dataset.collapsed = String(collapsed);
    chevron.textContent = collapsed ? "▸" : "▾";
  });

  return {
    root,
    body,
    head,
    setTitle: (text) => {
      label.textContent = text;
    },
    setBadge: (text, kind) => {
      badge.hidden = text === null;
      badge.textContent = text ?? "";
      badge.className = `badge${kind ? ` ${kind}` : ""}`;
    },
  };
}

export interface SliderOptions {
  min: number;
  max: number;
  step: number;
  value: number;
  format?: (value: number) => string;
}

/** Labelled range input with a live value readout. */
export function slider(
  label: string,
  options: SliderOptions,
  onChange: (value: number) => void,
): HTMLElement {
  const readout = el("span", { text: (options.format ?? String)(options.value) });
  const input = el("input", {
    type: "range",
    min: options.min,
    max: options.max,
    step: options.step,
    value: options.value,
  });
  input.addEventListener("input", () => {
    const value = Number(input.value);
    readout.textContent = (options.format ?? String)(value);
    onChange(value);
  });
  return el(
    "div",
    { class: "field" },
    el("span", {}, el("span", { text: label }), " ", readout),
    input,
  );
}

export function checkbox(label: string, checked: boolean, onChange: (value: boolean) => void): HTMLElement {
  const input = el("input", { type: "checkbox", ...(checked ? { checked: true } : {}) });
  input.addEventListener("change", () => onChange(input.checked));
  return el("label", { class: "row" }, input, el("span", { class: "grow", text: label }));
}

export function selectBox<T extends string>(
  label: string,
  values: Array<{ value: T; label: string }>,
  current: T,
  onChange: (value: T) => void,
): HTMLElement {
  const select = el("select");
  for (const option of values) {
    select.append(el("option", { value: option.value, ...(option.value === current ? { selected: true } : {}) }, option.label));
  }
  select.addEventListener("change", () => onChange(select.value as T));
  return el("div", { class: "field" }, el("span", { text: label }), select);
}

export function keyValue(pairs: Array<[string, string]>): HTMLElement {
  const list = el("dl", { class: "kv" });
  for (const [key, value] of pairs) {
    list.append(el("dt", { text: key }), el("dd", { text: value }));
  }
  return list;
}

export interface ProgressHandle {
  root: HTMLElement;
  set(fraction: number): void;
  remove(): void;
}

export function progressBar(): ProgressHandle {
  const fill = el("i");
  const root = el("div", { class: "progress" }, fill);
  return {
    root,
    set: (fraction) => {
      fill.style.width = `${Math.max(0, Math.min(1, fraction)) * 100}%`;
    },
    remove: () => root.remove(),
  };
}

export type ToastKind = "info" | "ok" | "warn" | "err";

/** Transient message in the corner; errors stay until dismissed. */
export function toast(message: string, kind: ToastKind = "info", timeoutMs = 6000): void {
  const host = document.getElementById("toasts");
  if (!host) return;
  const node = el("div", { class: `toast ${kind}`, text: message });
  node.addEventListener("click", () => node.remove());
  host.append(node);
  if (kind !== "err") setTimeout(() => node.remove(), timeoutMs);
}

export interface ModalHandle {
  body: HTMLElement;
  footer: HTMLElement;
  close(): void;
}

export function modal(title: string, options: { onClose?: () => void } = {}): ModalHandle {
  const root = document.getElementById("modal-root");
  if (!root) throw new Error("modal root missing");
  const body = el("div", { class: "modal-body" });
  const footer = el("footer");
  const closeButton = el("button", { class: "icon", type: "button", title: "Close" }, "✕");
  const dialog = el(
    "div",
    { class: "modal", role: "dialog", "aria-modal": "true" },
    el("header", {}, el("span", { class: "grow", text: title }), closeButton),
    body,
    footer,
  );
  const backdrop = el("div", { class: "modal-backdrop" }, dialog);

  const close = () => {
    backdrop.remove();
    document.removeEventListener("keydown", onKey);
    options.onClose?.();
  };
  const onKey = (event: KeyboardEvent) => {
    if (event.key === "Escape") close();
  };
  closeButton.addEventListener("click", close);
  backdrop.addEventListener("click", (event) => {
    if (event.target === backdrop) close();
  });
  document.addEventListener("keydown", onKey);
  root.append(backdrop);

  return { body, footer, close };
}
